import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as shipment_urls from '../../urls/api-shipments/merchant';
import config from '../../config';
import { isLocalLikeHost, resolveDashboardClientEnv } from '../../utils/environmentMode';
import { Modal, ModalBody, ModalFooter, Button, Alert } from 'reactstrap';
import axios from 'axios';
import gophrGMark from '../../assets/img/Gophr_G_RGB.png';
import PhotoGallery from './PhotoGallery';
import { UpgradeHint } from './featureLock';
import { extractInvoiceDisplayNumber, extractInvoiceLinks } from '../../services/paymentService';
const clientEnv = resolveDashboardClientEnv();
const PRODUCTION_DASHBOARD_HOST = 'dashboard.gophr.app';
const PRODUCTION_SHIPMENTS_HOST = 'https://api-shipments.gophr.app';
const PRODUCTION_FILES_HOST = 'https://api-files.gophr.app';
const SHIPMENT_DOCUMENTS_UPDATED_EVENT = 'shipment-documents-updated';
const FILE_VIEWER_FETCH_TIMEOUT_MS = 12000;
const FILE_VIEWER_DOCUMENT_FETCH_TIMEOUT_MS = 20000;
const FILE_VIEWER_CACHE_TTL_MS = 30000;
const fileViewerDocumentsCacheByKey = new Map();
const fileViewerModalOpenStateByKey = new Map();

const buildFileViewerModalStateKey = ({ shipmentId, shipFriendId = '' }) => {
    if (shipmentId) {
        return `shipment:${shipmentId}`;
    }

    return `friendly:${shipFriendId || ''}`;
};

const isProductionDashboardBrowser = () =>
    typeof window !== 'undefined' && window.location && window.location.hostname === PRODUCTION_DASHBOARD_HOST;

const resolveProductionBrowserHost = (configuredHost, productionHost) => {
    if (!isProductionDashboardBrowser()) {
        return configuredHost;
    }

    if (typeof configuredHost === 'string' && configuredHost.includes('dev-api-')) {
        return productionHost;
    }

    return configuredHost || productionHost;
};

const resolveFileViewerShipmentHost = configuredHost => {
    const productionAwareHost = resolveProductionBrowserHost(configuredHost, PRODUCTION_SHIPMENTS_HOST);

    if (isLocalLikeHost(productionAwareHost) && !isLocalLikeHost(shipment_urls.hasuraHost)) {
        return shipment_urls.hostedShipmentHost || productionAwareHost;
    }

    return productionAwareHost || shipment_urls.hostedShipmentHost || shipment_urls.shipmentHost;
};

const buildShipmentDocumentHostAttempts = primaryHost => {
    const candidates = [
        primaryHost,
        shipment_urls.shipmentHost,
        shipment_urls.hostedShipmentHost,
        PRODUCTION_SHIPMENTS_HOST,
    ].filter(candidate => typeof candidate === 'string' && candidate.trim() !== '');

    return [...new Set(candidates.map(candidate => candidate.trim()))];
};

const getFileViewerFetchKey = (shipmentHost, shipmentId) => `${shipmentHost || ''}|${shipmentId || ''}`;

const readCachedFileViewerFetch = cacheKey => {
    const cachedEntry = fileViewerDocumentsCacheByKey.get(cacheKey);

    if (!cachedEntry) {
        return null;
    }

    if ((Date.now() - cachedEntry.cachedAt) > FILE_VIEWER_CACHE_TTL_MS) {
        fileViewerDocumentsCacheByKey.delete(cacheKey);
        return null;
    }

    return cachedEntry.result;
};

const writeCachedFileViewerFetch = (cacheKey, result) => {
    fileViewerDocumentsCacheByKey.set(cacheKey, {
        cachedAt: Date.now(),
        result,
    });
};

const getResponseStatusValue = response => {
    if (!response || !response.data) {
        return '';
    }

    if (response.data.status !== undefined && response.data.status !== null) {
        return response.data.status;
    }

    if (response.data.status_code !== undefined && response.data.status_code !== null) {
        return response.data.status_code;
    }

    return response.status || '';
};

const normalizeDocumentList = response => {
    if (!response || !response.data) {
        return [];
    }

    if (Array.isArray(response.data.payload)) {
        return response.data.payload;
    }

    if (Array.isArray(response.data)) {
        return response.data;
    }

    return [];
};

const isSuccessfulDocumentResponse = responseStatus => (
    responseStatus === 'successful'
    || responseStatus === 'success'
    || responseStatus === 200
    || responseStatus === '200'
);

const requestShipmentDocumentsByHost = async ({ attemptedHost, shipmentId }) => {
    const requestUrl = `${attemptedHost}/shipments/${shipmentId}/documents`;

    const response = await runWithAuthRetry(token => ({
        method: 'get',
        headers: {
            Authorization: token ? 'Bearer ' + token : '',
            'content-type': 'application/json'
        },
        url: requestUrl,
        timeout: FILE_VIEWER_DOCUMENT_FETCH_TIMEOUT_MS,
    }));

    return { response, requestUrl };
};

const fetchShipmentDocumentsSnapshot = async ({ shipmentHost, shipmentId }) => {
    const hostAttempts = buildShipmentDocumentHostAttempts(shipmentHost);
    const attemptPromises = hostAttempts.map(attemptedHost =>
        requestShipmentDocumentsByHost({ attemptedHost, shipmentId })
            .then(({ response, requestUrl }) => ({
                responseStatus: getResponseStatusValue(response),
                payload: normalizeDocumentList(response),
                hydratedShipment: null,
                hostUsed: attemptedHost,
                endpointUsed: requestUrl,
            }))
    );

    try {
        return await Promise.any(attemptPromises);
    } catch (aggregateError) {
        const attemptedHostsLabel = hostAttempts.join(', ');
        throw new Error(`Unable to load shipment documents quickly. Hosts tried: ${attemptedHostsLabel}.`);
    }
};

const getSharedShipmentDocumentsSnapshot = async ({ shipmentHost, shipmentId }) => {
    const cacheKey = getFileViewerFetchKey(shipmentHost, shipmentId);
    const cachedResult = readCachedFileViewerFetch(cacheKey);

    if (cachedResult) {
        return {
            ...cachedResult,
            source: 'cache',
        };
    }

    const freshResult = await fetchShipmentDocumentsSnapshot({ shipmentHost, shipmentId });
    writeCachedFileViewerFetch(cacheKey, freshResult);
    return {
        ...freshResult,
        source: 'network',
    };
};

const getApiFilesHost = () => resolveProductionBrowserHost(config.config.api_files.host, PRODUCTION_FILES_HOST);

const getAuthTokenCandidates = () => {
    const accessToken = (localStorage.getItem('access_token') || '').trim();
    const idToken = (localStorage.getItem('id_token') || '').trim();
    const candidates = [accessToken, idToken].filter(Boolean);

    return [...new Set(candidates)];
};

const runWithAuthRetry = async buildRequestConfig => {
    const authTokenCandidates = getAuthTokenCandidates();
    const fallbackCandidates = authTokenCandidates.length > 0 ? authTokenCandidates : [''];
    let lastError = null;

    for (let index = 0; index < fallbackCandidates.length; index += 1) {
        const token = fallbackCandidates[index];

        try {
            return await axios(buildRequestConfig(token));
        } catch (requestError) {
            lastError = requestError;
            const statusCode = requestError && requestError.response && requestError.response.status;
            const hasMoreCandidates = index < fallbackCandidates.length - 1;

            if (!(statusCode === 401 && hasMoreCandidates)) {
                throw requestError;
            }
        }
    }

    throw lastError || new Error('Unable to complete authenticated request.');
};

const isBolFile = file => {
    const fileName = String((file && file.file_name) || '').toUpperCase();
    return file && (file.doc_type === 'bill_of_lading' || fileName.includes('GOPHR-BOL-'));
};

const getDocumentFileName = file => {
    const rawFileName = file && file.file_name ? file.file_name : '';

    try {
        return decodeURIComponent(rawFileName);
    } catch (error) {
        return rawFileName;
    }
};

const formatBolLabelDate = (file, shipment) => {
    const dateSource =
        (file && (file.created_at || file.updated_at)) ||
        (shipment && (shipment.completed_at || shipment.updated_at || shipment.created_at)) ||
        null;

    const parsedDate = dateSource ? new Date(dateSource) : new Date();

    if (Number.isNaN(parsedDate.getTime())) {
        const fallbackDate = new Date();
        return `${fallbackDate.getMonth() + 1}-${fallbackDate.getDate()}-${fallbackDate.getFullYear()}`;
    }

    return `${parsedDate.getMonth() + 1}-${parsedDate.getDate()}-${parsedDate.getFullYear()}`;
};

const getStopReceiverName = locationBlob => {
    if (!locationBlob) {
        return '';
    }

    const firstName = typeof locationBlob.first_name === 'string' ? locationBlob.first_name.trim() : '';
    const lastName = typeof locationBlob.last_name === 'string' ? locationBlob.last_name.trim() : '';
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();

    if (fullName) {
        return fullName;
    }

    if (typeof locationBlob.name === 'string' && locationBlob.name.trim()) {
        return locationBlob.name.trim();
    }

    if (typeof locationBlob.business_name === 'string' && locationBlob.business_name.trim()) {
        return locationBlob.business_name.trim();
    }

    return '';
};

const getDocumentDisplayName = (file, shipment) => {
    const fileName = getDocumentFileName(file);

    if (!shipment || !isBolFile(file) || !/_\d+\.pdf$/i.test(fileName)) {
        return fileName;
    }

    const orders = Array.isArray(shipment.Orders) ? shipment.Orders : [];
    const dropoffOrders = orders.filter(order => order && order.actual_action === 'dropoff');
    const stopMatch = fileName.match(/_(\d+)\.pdf$/i);
    const stopNumber = stopMatch ? Number(stopMatch[1]) : null;
    const stopOrder = stopNumber ? dropoffOrders[stopNumber - 1] : null;
    const locationBlob = (stopOrder && stopOrder.location_blob) || {};
    const stopLabel = getStopReceiverName(locationBlob) || (stopNumber ? `Stop ${stopNumber}` : 'Stop');

    return `${stopLabel} BOL-${formatBolLabelDate(file, shipment)}`;
};

const getBolStopNumber = file => {
    const fileName = getDocumentFileName(file);
    const stopMatch = fileName.match(/_(\d+)\.pdf$/i);
    return stopMatch ? Number(stopMatch[1]) : null;
};

const isGoPilotReportFile = file => {
    const fileName = String(getDocumentFileName(file) || '').toUpperCase();
    return file && (file.doc_type === 'gopilot_report' || fileName.includes('GOPHR-GOPILOT-REPORT-'));
};

const getDocumentFreshnessTimestamp = file => {
    const candidateTimestamp = file && (file.updated_at || file.created_at || file.expires_at);
    const parsedTimestamp = candidateTimestamp ? new Date(candidateTimestamp).getTime() : Number.NaN;

    if (Number.isFinite(parsedTimestamp)) {
        return parsedTimestamp;
    }

    const numericTimestamp = Number(candidateTimestamp);
    return Number.isFinite(numericTimestamp) ? numericTimestamp : 0;
};

const dedupeShipmentFiles = files => {
    if (!Array.isArray(files) || files.length <= 1) {
        return Array.isArray(files) ? files : [];
    }

    const uniqueFiles = [];
    const newestBolByStop = new Map();
    const newestGoPilotReportByFileName = new Map();

    files.forEach(file => {
        const freshnessTimestamp = getDocumentFreshnessTimestamp(file);

        if (isBolFile(file)) {
            const stopNumber = getBolStopNumber(file);
            const stopKey = stopNumber != null ? String(stopNumber) : getDocumentFileName(file);
            const existingFile = newestBolByStop.get(stopKey);

            if (!existingFile || freshnessTimestamp >= getDocumentFreshnessTimestamp(existingFile)) {
                newestBolByStop.set(stopKey, file);
            }

            return;
        }

        if (isGoPilotReportFile(file)) {
            const reportKey = getDocumentFileName(file) || file.short_code || file.signed_url;
            const existingFile = newestGoPilotReportByFileName.get(reportKey);

            if (!existingFile || freshnessTimestamp >= getDocumentFreshnessTimestamp(existingFile)) {
                newestGoPilotReportByFileName.set(reportKey, file);
            }

            return;
        }

        uniqueFiles.push(file);
    });

    return [
        ...uniqueFiles,
        ...Array.from(newestBolByStop.values()),
        ...Array.from(newestGoPilotReportByFileName.values()),
    ];
};

const getFileCacheKey = file => file.file_id || file.short_code || file.signed_url || file.file_name;

const getResolvedFileUrl = file => {
    if (file && file.signed_url) {
        return file.signed_url;
    }

    if (isBolFile(file) && file && file.short_code) {
        // Fallback only: short_code can be shared by multiple BOL rows on one shipment.
        return `${getApiFilesHost()}/d/${encodeURIComponent(file.short_code)}`;
    }

    return '';
};

const buildInvoiceDocumentEntry = shipment => {
    const invoiceLinks = extractInvoiceLinks(shipment);
    if (!invoiceLinks) {
        return null;
    }

    const url = invoiceLinks.hostedUrl || invoiceLinks.pdfUrl;
    if (!url) {
        return null;
    }

    const displayNumber = extractInvoiceDisplayNumber(shipment);

    return {
        id: `invoice-${displayNumber || 'available'}`,
        label: `Invoice # ${displayNumber || 'Available'}`,
        url,
    };
};

const fetchShipmentDocumentContext = async shipmentId => {
    if (!shipmentId) {
        return null;
    }

    const response = await runWithAuthRetry(token => ({
        method: 'post',
        url: shipment_urls.hasuraHost,
        headers: {
            Authorization: token ? 'Bearer ' + token : '',
            'content-type': 'application/json'
        },
        timeout: FILE_VIEWER_FETCH_TIMEOUT_MS,
        data: {
            query: `query FileViewerShipmentContext($shipmentId: uuid!) {
                Shipments(where: {shipment_id: {_eq: $shipmentId}}, limit: 1) {
                    shipment_id
                    shipment_friendly_id
                    created_at
                    updated_at
                    completed_at
                    billing_details
                    invoicing_errors
                    Orders(order_by: { order_friendly_id: asc }) {
                        order_id
                        order_friendly_id
                        actual_action
                        location_blob
                    }
                }
            }`,
            variables: { shipmentId }
        }
    }));

    const shipments = response && response.data && response.data.data ? response.data.data.Shipments : null;
    return Array.isArray(shipments) && shipments.length > 0 ? shipments[0] : null;
};

const isPresignedFileUrl = url =>
    typeof url === 'string' && /[?&](X-Goog-Algorithm|X-Goog-Signature|X-Amz-Algorithm|X-Amz-Signature)=/i.test(url);

const getCacheBustedFileUrl = file => {
    const rawUrl = getResolvedFileUrl(file);
    if (!rawUrl) {
        return rawUrl;
    }

    if (isPresignedFileUrl(rawUrl)) {
        return rawUrl;
    }

    const cacheKey = [
        file && file.updated_at,
        file && file.created_at,
        file && file.expires_at,
        file && file.short_code,
        file && file.file_name,
    ].filter(Boolean).join(':');

    if (!cacheKey) {
        return rawUrl;
    }

    const separator = rawUrl.includes('?') ? '&' : '?';
    return `${rawUrl}${separator}preview_key=${encodeURIComponent(cacheKey)}`;
};

const sortShipmentFiles = files => {
    return [...files].sort((left, right) => {
        const leftStopNumber = getBolStopNumber(left);
        const rightStopNumber = getBolStopNumber(right);

        if (leftStopNumber != null && rightStopNumber != null) {
            return leftStopNumber - rightStopNumber;
        }

        if (leftStopNumber != null) {
            return -1;
        }

        if (rightStopNumber != null) {
            return 1;
        }

        const leftCreatedAt = left && left.created_at ? new Date(left.created_at).getTime() : 0;
        const rightCreatedAt = right && right.created_at ? new Date(right.created_at).getTime() : 0;

        return rightCreatedAt - leftCreatedAt;
    });
};

const getPickupActionInstruction = order => {
    if (!order || !order.actions || !Object.prototype.hasOwnProperty.call(order.actions, 'pick_up')) {
        return '';
    }

    const pickupAction = order.actions.pick_up;

    if (Array.isArray(pickupAction) && typeof pickupAction[0] === 'string') {
        return pickupAction[0];
    }

    if (typeof pickupAction === 'string') {
        return pickupAction;
    }

    return '';
};

const isPickupOrderForDisplay = order => {
    if (!order || !order.actions) {
        return false;
    }

    if (order.actual_action === 'pickup') {
        return true;
    }

    const pickupInstruction = getPickupActionInstruction(order);

    return Object.prototype.hasOwnProperty.call(order.actions, 'pick_up') && !pickupInstruction.includes('(drop)');
};

const buildOrderPhotoGroup = (label, order) => {
    const photos = Array.isArray(order && order.photos) ? order.photos.filter(photo => photo && photo.url) : [];

    if (photos.length === 0) {
        return null;
    }

    return {
        id: label.toLowerCase().replace(/\s+/g, '-'),
        label,
        photos,
    };
};

const getShipmentPhotoGroups = shipment => {
    const orders = Array.isArray(shipment && shipment.Orders) ? shipment.Orders.filter(Boolean) : [];

    if (orders.length === 0) {
        return [];
    }

    const pickupOrder = orders.find(isPickupOrderForDisplay) || orders[0] || null;
    const dropoffOrder = [...orders].reverse().find(
        order => order && order.actions && (Object.prototype.hasOwnProperty.call(order.actions, 'drop_off') || order.actual_action === 'dropoff')
    ) || (orders.find(order => order && order !== pickupOrder) || null);

    return [buildOrderPhotoGroup('Pickup Photos', pickupOrder), buildOrderPhotoGroup('Dropoff Photos', dropoffOrder)].filter(Boolean);
};

const FileViewer = ({
    shipmentId,
    showGenerateBolButton,
    deadDropPhotos = [],
    photoGroups = [],
    shipFriendId = '',
    shipment = null,
    includeStopPhotoGroups = false,
    onModalStateChange = null,
    lockGoPilotReports = false,
    shipmentHost = shipment_urls.shipmentHost
}) => {
    const modalStateKey = useMemo(() => buildFileViewerModalStateKey({ shipmentId, shipFriendId }), [shipmentId, shipFriendId]);
    const effectiveShipmentHost = resolveFileViewerShipmentHost(shipmentHost);
    const [isOpen, setIsOpen] = useState(() => fileViewerModalOpenStateByKey.get(modalStateKey) === true);
    //   const [fileUrl, setFileUrl] = useState(null);
    const [viewFileModal, setViewFileModal] = useState(false);
    const [files, setFiles] = useState([]); // Array of files
    const [selectedFile, setSelectedFile] = useState(null); // Currently selected file
    const [selectedFileName, setSelectedFileName] = useState('');
    const [fileType, setFileType] = useState(null);
    const [fetchError, setFetchError] = useState(null);
    const [isFetchingFiles, setIsFetchingFiles] = useState(false);
    const [noFileAvailable, setNoFileAvailable] = useState(false);
    const [loadingGenerate, setLoadingGenerate] = useState(false); // Track loading state for `generateBol`
    const [previewLoading, setPreviewLoading] = useState(false);
    const [resolvedBolTitles, setResolvedBolTitles] = useState({});
    const [resolvedShipment, setResolvedShipment] = useState(shipment || null);
    const isMountedRef = useRef(true);
    const requestIdRef = useRef(0);
    const resolvedShipmentRef = useRef(resolvedShipment || null);
    const shipmentPropRef = useRef(shipment || null);
    const deadDropPhotosSignature = Array.isArray(deadDropPhotos) ? deadDropPhotos.filter(Boolean).join('|') : '';
    const photoGroupsSignature = Array.isArray(photoGroups)
        ? photoGroups
            .map(group => {
                const groupId = group && group.id ? String(group.id) : '';
                const photoCount = Array.isArray(group && group.photos) ? group.photos.length : 0;
                return `${groupId}:${photoCount}`;
            })
            .join('|')
        : '';
    const normalizedDeadDropPhotos = useMemo(
        () => (Array.isArray(deadDropPhotos) ? deadDropPhotos.filter(Boolean) : []),
        [deadDropPhotosSignature]
    );
    const normalizedPhotoGroups = useMemo(
        () => (Array.isArray(photoGroups) ? photoGroups.filter(Boolean) : []),
        [photoGroupsSignature]
    );
    const invoiceDocumentEntry = buildInvoiceDocumentEntry(resolvedShipment);
    const supplementalPhotoGroups = [...normalizedPhotoGroups, ...(includeStopPhotoGroups ? getShipmentPhotoGroups(resolvedShipment) : [])];
    const fileLinkButtonStyle = {
        background: 'none',
        border: 'none',
        padding: 0,
        textDecoration: 'underline',
        color: '#007BFF',
        cursor: 'pointer',
        fontSize: 'inherit',
        fontFamily: 'inherit',
        textAlign: 'left'
    };

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
            requestIdRef.current += 1;
        };
    }, []);

    useEffect(() => {
        setResolvedShipment(shipment || null);
    }, [shipment]);

    useEffect(() => {
        setIsOpen(fileViewerModalOpenStateByKey.get(modalStateKey) === true);
    }, [modalStateKey]);

    useEffect(() => {
        fileViewerModalOpenStateByKey.set(modalStateKey, isOpen);
    }, [modalStateKey, isOpen]);

    useEffect(() => {
        resolvedShipmentRef.current = resolvedShipment || null;
    }, [resolvedShipment]);

    useEffect(() => {
        shipmentPropRef.current = shipment || null;
    }, [shipment]);

    useEffect(() => {
        if (typeof document === 'undefined' || !document.body || !document.body.classList) {
            return undefined;
        }

        const isRealtimeInteractionActive = isOpen || viewFileModal;

        if (isRealtimeInteractionActive) {
            document.body.classList.add('dispatch-realtime-interaction-active');
        } else {
            document.body.classList.remove('dispatch-realtime-interaction-active');
        }

        return () => {
            document.body.classList.remove('dispatch-realtime-interaction-active');
        };
    }, [isOpen, viewFileModal]);

    const fetchFileUrl = useCallback(async () => {
        const requestId = requestIdRef.current + 1;
        requestIdRef.current = requestId;

        if (isMountedRef.current) {
            setFetchError(null); // Reset error state
            setNoFileAvailable(false); // Reset no-file state
            setIsFetchingFiles(true);
        }

        try {
            if (!shipmentId) {
                return [];
            }

            let sharedSnapshot = null;
            let documentFetchAttempt = 0;
            let lastDocumentFetchError = null;

            while (documentFetchAttempt < 2 && !sharedSnapshot) {
                documentFetchAttempt += 1;
                try {
                    sharedSnapshot = await getSharedShipmentDocumentsSnapshot({
                        shipmentHost: effectiveShipmentHost,
                        shipmentId,
                    });
                } catch (documentFetchError) {
                    lastDocumentFetchError = documentFetchError;
                    if (documentFetchAttempt < 2) {
                        await new Promise(resolve => setTimeout(resolve, 250));
                    }
                }
            }

            if (!sharedSnapshot) {
                throw lastDocumentFetchError || new Error('Unable to fetch shipment documents.');
            }

            const hydratedShipment = sharedSnapshot.hydratedShipment;
            let fetchedFiles = isSuccessfulDocumentResponse(sharedSnapshot.responseStatus) ? sharedSnapshot.payload : [];

            if (normalizedDeadDropPhotos.length > 0) {
                fetchedFiles.push({ file_name: 'Proof of Delivery Photo', signed_url: normalizedDeadDropPhotos[0] });
            }

            if (!isMountedRef.current || requestId !== requestIdRef.current) {
                return fetchedFiles;
            }

            const nextResolvedShipment = hydratedShipment || resolvedShipmentRef.current || shipmentPropRef.current || null;

            if (hydratedShipment) {
                setResolvedShipment(currentShipment => ({
                    ...(currentShipment || {}),
                    ...hydratedShipment,
                    Orders: Array.isArray(hydratedShipment.Orders) ? hydratedShipment.Orders : (currentShipment && currentShipment.Orders) || []
                }));
            }

            const derivedInvoiceDocumentEntry = buildInvoiceDocumentEntry(nextResolvedShipment);
            const derivedSupplementalPhotoGroups = [
                ...normalizedPhotoGroups,
                ...(includeStopPhotoGroups ? getShipmentPhotoGroups(nextResolvedShipment) : [])
            ];
            const hasSupplementalEntriesNow = normalizedDeadDropPhotos.length > 0 || derivedSupplementalPhotoGroups.length > 0;

            fetchedFiles = sortShipmentFiles(dedupeShipmentFiles(fetchedFiles));

            if (fetchedFiles.length > 0) {
                setFiles(fetchedFiles);
                setResolvedBolTitles({});
            } else if (derivedInvoiceDocumentEntry || hasSupplementalEntriesNow) {
                setFiles([]);
                setNoFileAvailable(false);
                setResolvedBolTitles({});
            } else {
                setNoFileAvailable(true);
                setResolvedBolTitles({});
            }

            if (!hydratedShipment) {
                void fetchShipmentDocumentContext(shipmentId)
                    .then(backgroundShipment => {
                        if (!isMountedRef.current || requestId !== requestIdRef.current || !backgroundShipment) {
                            return;
                        }

                        setResolvedShipment(currentShipment => ({
                            ...(currentShipment || {}),
                            ...backgroundShipment,
                            Orders: Array.isArray(backgroundShipment.Orders)
                                ? backgroundShipment.Orders
                                : (currentShipment && currentShipment.Orders) || []
                        }));
                    })
                    .catch(backgroundShipmentError => {
                        void backgroundShipmentError;
                    });
            }

            return fetchedFiles;
        } catch (error) {
            if (isMountedRef.current && requestId === requestIdRef.current) {
                setFetchError('An error occurred while fetching the files.'); // Set fetch error message
            }
            return [];
        } finally {
            if (isMountedRef.current && requestId === requestIdRef.current) {
                setIsFetchingFiles(false);
            }
        }
    }, [effectiveShipmentHost, includeStopPhotoGroups, normalizedDeadDropPhotos, normalizedPhotoGroups, shipmentId]);

    useEffect(() => {
        if (isOpen) {
            fetchFileUrl();
        }
    }, [fetchFileUrl, isOpen]);

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.addEventListener !== 'function') {
            return undefined;
        }

        const handleDocumentsUpdated = event => {
            const detail = event && event.detail ? event.detail : {};
            const matchesShipmentId = detail.shipmentId && detail.shipmentId === shipmentId;
            const matchesFriendlyId = detail.shipmentFriendlyId && detail.shipmentFriendlyId === shipFriendId;

            if (!isOpen || (!matchesShipmentId && !matchesFriendlyId)) {
                return;
            }

            if (detail.uploadedUrl) {
                setFiles(previousFiles => {
                    const optimisticFile = {
                        doc_type: detail.uploadedDocType || 'admin_message_attachment',
                        file_name: detail.uploadedFileName || 'Uploaded file',
                        signed_url: detail.uploadedUrl,
                        created_at: new Date().toISOString(),
                    };

                    const exists = previousFiles.some(file => String((file && file.signed_url) || '') === String(detail.uploadedUrl));
                    if (exists) {
                        return previousFiles;
                    }

                    return sortShipmentFiles(dedupeShipmentFiles([optimisticFile, ...previousFiles]));
                });
            }

            void fetchFileUrl();
        };

        window.addEventListener(SHIPMENT_DOCUMENTS_UPDATED_EVENT, handleDocumentsUpdated);

        return () => {
            window.removeEventListener(SHIPMENT_DOCUMENTS_UPDATED_EVENT, handleDocumentsUpdated);
        };
    }, [fetchFileUrl, isOpen, shipFriendId, shipmentId]);

    const generateBol = async () => {
        if (isMountedRef.current) {
            setLoadingGenerate(true); // Show loading state
        }
        try {
            await axios({
                method: 'post',
                headers: {
                    Authorization: 'Bearer ' + localStorage.getItem('access_token'),
                    'content-type': 'multipart/form-data'
                },
                url: getApiFilesHost() + `/shipments/${shipmentId}/bol/generate?client_env=${clientEnv}`
            });

            // Auto-refresh the file list after success
            await fetchFileUrl();
        } catch (error) {
            if (isMountedRef.current) {
                setFetchError('Unable to generate BOL right now.');
            }
        } finally {
            if (isMountedRef.current) {
                setLoadingGenerate(false); // Reset loading state
            }
        }
    };

    const handleOpenModal = async () => {
        setIsOpen(true);
        if (typeof onModalStateChange === 'function') {
            onModalStateChange(true);
        }
    };

    const handleCloseModal = () => {
        requestIdRef.current += 1;
        setIsOpen(false);
        if (typeof onModalStateChange === 'function') {
            onModalStateChange(false);
        }
        setSelectedFile(null);
        setSelectedFileName('');
        setFileType(null);
        setFetchError(null);
        setIsFetchingFiles(false);
        setNoFileAvailable(false);
        setResolvedBolTitles({});
    };

    const handleOpenFileModal = file => {
        setSelectedFile(getCacheBustedFileUrl(file));
        setSelectedFileName(getDocumentFileName(file));
        setFileType(file.file_name.endsWith('.pdf') || file.file_name.endsWith('.PDF') ? 'pdf' : 'image');
        setPreviewLoading(true);
        setViewFileModal(true);
    };

    const handleCloseFileModal = () => {
        setViewFileModal(false);
        setSelectedFile(null);
        setSelectedFileName('');
        setFileType(null);
        setPreviewLoading(false);
    };

    const handleSaveFile = async () => {
        if (!selectedFile) {
            setFetchError('No file selected.');
            return;
        }

        if (fileType === 'pdf') {
            const link = document.createElement('a');
            link.href = selectedFile;
            link.download = selectedFileName || selectedFile.substring(selectedFile.lastIndexOf('/') + 1).split('?')[0];
            link.click();
        } else if (fileType === 'image') {
            try {
                const response = await fetch(selectedFile, { mode: 'cors' });

                if (!response.ok) {
                    throw new Error('Failed to fetch image');
                }

                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob);

                const link = document.createElement('a');
                link.href = blobUrl;
                link.download = 'Proof of Delivery Photo - SHIP_ID - ' + shipFriendId;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(blobUrl);
            } catch (error) {
                alert('Image download failed due to CORS restrictions.');
            }
        } else {
            setFetchError('Unsupported file type.');
        }
    };

    const handleOpenInvoiceLink = () => {
        if (!invoiceDocumentEntry || !invoiceDocumentEntry.url) {
            return;
        }

        window.open(invoiceDocumentEntry.url, '_blank', 'noopener,noreferrer');
    };

    return (
        <div>
            <Button
                href="#"
                style={{
                    backgroundColor: '#ff8d00',
                    borderColor: '#ff8d00',
                    padding: '0.55rem 1.1rem',
                    textDecoration: 'none',
                    color: '#ffffff',
                    cursor: 'pointer',
                    fontSize: 'inherit',
                    fontFamily: 'inherit',
                    fontWeight: 600,
                    textAlign: 'center'
                }}
                onClick={e => {
                    e.preventDefault();
                    handleOpenModal();
                }}
            >
                View Documents
            </Button>

            {/* Modal to list files */}
            <div>
                {/* Inline style block */}
                <style>
                    {`
                      .file-link {
                        text-decoration: underline;
                        color: #007BFF; /* Initial blue color */
                        font-size: inherit;
                        font-family: inherit;
                        background: none;
                        border: none;
                        padding: 0;
                        cursor: pointer;
                      }

                      .file-link:hover {
                        color: #C71585; /* Dark pink on hover */
                        background: none !important; /* Ensure no background color on hover */
                      }

                                            .document-preview-shell {
                                                position: relative;
                                                min-height: 70vh;
                                                background: linear-gradient(180deg, #ffffff 0%, #fff7ef 100%);
                                                border-radius: 18px;
                                                overflow: hidden;
                                            }

                                            .document-preview-loader {
                                                position: absolute;
                                                inset: 0;
                                                display: flex;
                                                flex-direction: column;
                                                align-items: center;
                                                justify-content: center;
                                                gap: 14px;
                                                background: rgba(255, 255, 255, 0.92);
                                                z-index: 2;
                                            }

                                            .document-preview-loader img {
                                                width: 72px;
                                                height: 72px;
                                                animation: document-preview-spin 1.4s linear infinite;
                                                transform-origin: center center;
                                            }

                                            .document-preview-loader span {
                                                color: #1e1e2f;
                                                font-size: 16px;
                                                font-weight: 700;
                                                letter-spacing: 0.02em;
                                            }

                                            .document-preview-frame,
                                            .document-preview-image {
                                                display: block;
                                                width: 100%;
                                                min-height: 70vh;
                                                border: none;
                                                background: transparent;
                                            }

                                            .document-preview-image {
                                                max-height: 65vh;
                                                min-height: 0;
                                                height: auto;
                                                margin: 0 auto;
                                                object-fit: contain;
                                            }

                                            @keyframes document-preview-spin {
                                                from {
                                                    transform: rotate(0deg);
                                                }

                                                to {
                                                    transform: rotate(360deg);
                                                }
                                            }
                    `}
                </style>

                <Modal isOpen={isOpen} toggle={handleCloseModal} size="lg" className="dispatch-realtime-interaction" style={{ minWidth: '400px', top: '20vh' }}>
                    <ModalBody>
                        {fetchError ? (
                            <Alert color="danger" style={{ width: '100%', textAlign: 'center' }}>
                                {fetchError}
                            </Alert>
                        ) : noFileAvailable ? (
                            <Alert color="info" style={{ width: '100%', textAlign: 'center' }}>
                                No files are available for this shipment currently, you can upload or check it later
                            </Alert>
                        ) : (
                            <div>
                                {files.length > 0 || invoiceDocumentEntry || supplementalPhotoGroups.length > 0 ? (
                                    <ul style={{ paddingLeft: '20px', listStyleType: 'none' }}>
                                        {files.map((file, index) => {
                                            const goPilotLocked = lockGoPilotReports && isGoPilotReportFile(file);
                                            return (
                                            <li key={file.file_id || file.signed_url || file.file_name || `file-${index}`} style={{ marginBottom: '10px' }}>
                                                {goPilotLocked ? (
                                                    <>
                                                        <Button className="file-link" disabled style={{ opacity: 0.45, filter: 'grayscale(1)', cursor: 'not-allowed' }}>
                                                            {getDocumentDisplayName(file, resolvedShipment)}
                                                        </Button>
                                                        <UpgradeHint label="Upgrade to Pro to access Go-Pilot reports" />
                                                    </>
                                                ) : (
                                                    <Button
                                                        className="file-link" // Apply the CSS class
                                                        onClick={() => handleOpenFileModal(file)}
                                                    >
                                                        {resolvedBolTitles[getFileCacheKey(file)] || getDocumentDisplayName(file, resolvedShipment)}
                                                    </Button>
                                                )}
                                            </li>
                                            );
                                        })}
                                        {invoiceDocumentEntry ? (
                                            <li key={invoiceDocumentEntry.id} style={{ marginBottom: '10px' }}>
                                                <Button
                                                    className="file-link"
                                                    onClick={handleOpenInvoiceLink}
                                                >
                                                    {invoiceDocumentEntry.label}
                                                </Button>
                                            </li>
                                        ) : null}
                                        {supplementalPhotoGroups.map((group, index) => (
                                            <li key={group.id || `${group.label}-${index}`} style={{ marginBottom: '10px' }}>
                                                <PhotoGallery
                                                    photos={group.photos}
                                                    buttonText={group.label}
                                                    buttonStyle={fileLinkButtonStyle}
                                                    altTextPrefix={group.label}
                                                />
                                            </li>
                                        ))}
                                    </ul>
                                ) : isFetchingFiles ? (
                                    <Alert color="info" style={{ width: '100%', textAlign: 'center' }}>
                                        Loading files...
                                    </Alert>
                                ) : (
                                    <Alert color="info" style={{ width: '100%', textAlign: 'center' }}>
                                        No files are available for this shipment currently, you can upload or check it later
                                    </Alert>
                                )}
                            </div>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        {showGenerateBolButton && !files.some(isBolFile) && (
                            <Button color="primary" onClick={generateBol} disabled={loadingGenerate}>
                                {loadingGenerate ? 'Generating...' : 'Generate BOL'}
                            </Button>
                        )}
                        <Button color="danger" onClick={handleCloseModal} className="ml-auto">
                            Close
                        </Button>
                    </ModalFooter>
                </Modal>
            </div>

            {/* Modal to display the selected file */}
            <Modal
                isOpen={viewFileModal}
                toggle={handleCloseFileModal}
                size="lg"
                className="dispatch-realtime-interaction"
                style={{
                    maxWidth: '800px',
                    width: '95%',
                    margin: '30px auto'
                }}
            >
                <ModalBody
                    style={{
                        padding: '15px',
                        overflow: 'visible'
                    }}
                >
                    {selectedFile ? (
                        <div className="document-preview-shell">
                            {previewLoading ? (
                                <div className="document-preview-loader">
                                    <img src={gophrGMark} alt="Loading document" />
                                    <span>Loading document...</span>
                                </div>
                            ) : null}
                            {fileType === 'pdf' ? (
                                <iframe
                                    title={selectedFileName || 'PDF preview'}
                                    src={selectedFile}
                                    className="document-preview-frame"
                                    onLoad={() => setPreviewLoading(false)}
                                />
                            ) : (
                                <img
                                    src={selectedFile}
                                    alt="file content"
                                    className="document-preview-image"
                                    onLoad={() => setPreviewLoading(false)}
                                    onError={() => setPreviewLoading(false)}
                                />
                            )}
                        </div>
                    ) : (
                        <Alert color="info" style={{ width: '100%', textAlign: 'center' }}>
                            Loading file...
                        </Alert>
                    )}
                </ModalBody>
                <ModalFooter
                    style={{
                        position: 'sticky',
                        bottom: 0,
                        backgroundColor: 'white',
                        padding: '10px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}
                >
                    <Button color="primary" onClick={handleSaveFile} disabled={!selectedFile}>
                        Save
                    </Button>

                    <Button color="danger" onClick={handleCloseFileModal}>
                        Close
                    </Button>
                </ModalFooter>
            </Modal>
        </div>
    );
};

export default FileViewer;
